/*
 * Copyright (c) 2024-2025 Hardfin, Inc.
 */
import { CRMConfiguration, Organization } from '@/account/api/types';
import { SupportErrorBoundary } from '@/components/support-error-boundary';
import { HubSpotIcon } from '@/icons/hubspot-icon';
import { findIntegrationConfig } from '@/utilities/integration';
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  DialogActions,
  DialogContent,
  Link,
  Typography,
} from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';

interface Props {
  organization: Organization;
  onClose: () => void;
}

export function CRMPendingDialog(props: Props) {
  const [isConnected, setIsConnected] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const queryClient = useQueryClient();

  // Find the CRM config (HubSpot or Salesforce) from the unified array
  const crmConfig: CRMConfiguration | null =
    findIntegrationConfig(
      props.organization.integrationConfigurations,
      'HUBSPOT'
    ) ??
    findIntegrationConfig(props.organization.integrationConfigurations, 'SFDC');
  const hasNoCRMConnection = crmConfig === null;
  const hasPartiallyConnected =
    crmConfig !== null &&
    crmConfig.configurationState.allScopesPresent !== true;

  useEffect(() => {
    function handleMessage(event: MessageEvent) {
      if (event.origin !== import.meta.env.VITE_API_URL) {
        return;
      }
      try {
        const data = event.data;
        if (data.integration !== 'HUBSPOT') {
          return;
        }
        if (data.success) {
          setIsConnected(true);
          setErrorMessage('');
          // Invalidate the org settings, this will cause the configuration window to show after its connected
          queryClient.invalidateQueries({
            queryKey: ['organization', props.organization.key],
          });
        } else {
          setErrorMessage(data.error || 'Unknown error during HubSpot connect');
        }
      } catch (err) {
        console.error('Failed to parse HubSpot connection message:', err);
        setErrorMessage('Could not process HubSpot connection response');
      } finally {
        // Don't continue bubbling this event
        event.stopPropagation();
      }
    }

    window.addEventListener('message', handleMessage);
    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }, [queryClient, props.organization.key]);

  const handleClose = () => {
    setErrorMessage('');
    setIsConnected(false);
    props.onClose();
  };

  const handleConnectHubspot = () => {
    setErrorMessage('');
    setIsConnected(false);

    // Open popup window for OAuth flow
    const authUrl = `${import.meta.env.VITE_API_URL}/auth/hubspot/init`;
    const popup = window.open(
      authUrl,
      'hubspotConnect',
      'width=720,height=700,toolbar=no,location=yes,status=no,menubar=no,scrollbars=yes,resizable=no'
    );

    if (!popup) {
      setErrorMessage(
        'Could not open HubSpot connection window. Please ensure popups are allowed.'
      );
    }
  };
  return (
    <>
      <DialogContent dividers>
        <SupportErrorBoundary message="Something went wrong">
          {isConnected ? (
            <Alert severity={'success'}>CRM Connected</Alert>
          ) : hasPartiallyConnected ? (
            <Typography>
              The <b>{props.organization.name}</b> organization needs additional
              permissions to function correctly.
            </Typography>
          ) : (
            <Typography>
              The <b>{props.organization.name}</b> organization does not have a
              CRM configured.
            </Typography>
          )}
          {errorMessage != '' && (
            <Alert severity="error" sx={{ marginTop: 2 }}>
              {errorMessage}
            </Alert>
          )}

          {!isConnected && (
            <Alert severity="info" sx={{ marginY: 2 }}>
              <AlertTitle>HubSpot admin required</AlertTitle>
              You must be an administrator in your HubSpot account in order to
              connect the integration.
            </Alert>
          )}

          {!isConnected && (
            <Box
              display="grid"
              gridTemplateColumns=".25fr .5fr .25fr"
              justifyContent="center"
              columnGap={1}
              rowGap={2}
              marginY={2}
              alignItems="center"
            >
              <Box />
              <Button
                size="large"
                variant="outlined"
                startIcon={<HubSpotIcon fontSize="large" />}
                sx={{ justifyContent: 'center', whiteSpace: 'nowrap' }}
                onClick={handleConnectHubspot}
              >
                {hasPartiallyConnected
                  ? 'Reconnect HubSpot'
                  : 'Connect HubSpot'}
              </Button>
              <Box />
            </Box>
          )}

          {hasNoCRMConnection && (
            <Typography>
              Review the{' '}
              <Link href="https://guide.hardfin.com/manage-your-account/integrations#use-hubspot-as-your-crm-integration">
                HubSpot integration documentation
              </Link>{' '}
              or{' '}
              <Link href="mailto:support@hardfin.com">
                contact Hardfin support
              </Link>{' '}
              for configuration questions.
            </Typography>
          )}
        </SupportErrorBoundary>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Close</Button>
      </DialogActions>
    </>
  );
}
